import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Target, FloppyDisk, Calculator } from "@phosphor-icons/react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const FIELDS = [
  { k: "trir_target", l: "TRIR (objetivo — menor es mejor)", hint: "Total Recordable Incident Rate. Meta típica minera <1.0", step: 0.1 },
  { k: "ltifr_target", l: "LTIFR (objetivo — menor es mejor)", hint: "Lost-Time Injury Frequency Rate por 1M h·h. Meta <3.0", step: 0.1 },
  { k: "is_rate_target", l: "Índice de Severidad (IS)", hint: "Días perdidos × 1,000,000 / h·h. Meta <100", step: 1 },
  { k: "observations_month", l: "Observaciones/mes (mínimo)", hint: "Meta de reportes proactivos por mes", step: 1 },
  { k: "toolbox_month", l: "Charlas 5-min/mes (mínimo)", hint: "Meta de charlas toolbox por mes", step: 1 },
  { k: "inspections_month", l: "Inspecciones/mes (mínimo)", hint: "Meta de inspecciones planeadas por mes", step: 1 },
  { k: "actions_ontime_pct", l: "% Acciones cerradas a tiempo", hint: "Meta de cumplimiento. Estándar 90%", step: 1 },
  { k: "iper_high_max", l: "IPER alto/crítico (máx.)", hint: "Máximo tolerable de IPERs alto o crítico", step: 1 },
];

const _iso = (d) => new Date(d).toISOString().slice(0, 10);

/**
 * KPI Targets input — configurable objectives that flow to the General SGSSO
 * PDF report and dashboard indicator badges.
 */
export default function HseKpiTargets() {
  const { user } = useAuth();
  const canManage = ["admin", "manager"].includes(user?.role);
  const [form, setForm] = useState({});
  const [dirty, setDirty] = useState(false);

  // --- Base HH ("Horas-Hombre" del período) ---
  const [manHours, setManHours] = useState(200000);
  const [manHoursDirty, setManHoursDirty] = useState(false);
  const [lastAuto, setLastAuto] = useState(null);   // {at, formula, range}
  const [autoOpen, setAutoOpen] = useState(false);
  const today = new Date();
  const monthAgo = new Date(today); monthAgo.setDate(today.getDate() - 30);
  const [autoForm, setAutoForm] = useState({
    date_from: _iso(monthAgo), date_to: _iso(today),
    hours_per_shift: 12, shifts_per_day: 2, headcount_source: "hr",
  });
  const [autoPreview, setAutoPreview] = useState(null);
  const [autoBusy, setAutoBusy] = useState(false);

  const load = useCallback(() => {
    api.get("/hse/kpi_targets").then((r) => { setForm(r.data); setDirty(false); }).catch(() => {});
    api.get("/settings/safety").then((r) => {
      setManHours(Number(r.data.man_hours) || 200000);
      setManHoursDirty(false);
      if (r.data.man_hours_last_auto_at) {
        setLastAuto({
          at: r.data.man_hours_last_auto_at,
          formula: r.data.man_hours_last_auto_formula || "",
          range: r.data.man_hours_last_auto_range || null,
        });
      } else {
        setLastAuto(null);
      }
    }).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    try {
      const payload = Object.fromEntries(
        Object.entries(form).map(([k, v]) => [k, Number(v) || 0])
      );
      await api.put("/hse/kpi_targets", payload);
      toast.success("Objetivos guardados");
      setDirty(false);
    } catch (e) { toast.error(apiError(e)); }
  };

  const saveManHours = async () => {
    try {
      await api.put("/settings/safety", { man_hours: Number(manHours) || 0 });
      toast.success(`Base HH actualizada a ${Number(manHours).toLocaleString()} HH`);
      setManHoursDirty(false);
    } catch (e) { toast.error(apiError(e)); }
  };

  const previewAuto = async () => {
    setAutoBusy(true);
    try {
      const qs = new URLSearchParams({
        date_from: autoForm.date_from, date_to: autoForm.date_to,
        hours_per_shift: autoForm.hours_per_shift, shifts_per_day: autoForm.shifts_per_day,
        headcount_source: autoForm.headcount_source,
      }).toString();
      const r = await api.get(`/hse/man-hours/auto?${qs}`);
      setAutoPreview(r.data);
    } catch (e) { toast.error(apiError(e)); setAutoPreview(null); }
    finally { setAutoBusy(false); }
  };

  const applyAuto = () => {
    if (!autoPreview) return;
    setManHours(autoPreview.man_hours);
    setManHoursDirty(true);
    toast.success(`Base sugerida aplicada — presiona "Guardar base HH" para persistirla`);
  };

  return (
    <div className="space-y-4" data-testid="hse-kpi-targets">
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3">
        <Target size={22} weight="duotone" className="text-blue-900 flex-shrink-0 mt-0.5" />
        <div>
          <p className="font-bold text-blue-900">Objetivos de KPIs SGSSO</p>
          <p className="text-xs text-blue-800 mt-1">
            Configure los objetivos que la gerencia definió para el período. El reporte
            general contrasta los indicadores reales contra estos objetivos y marca con
            ✓/✗ cuáles están en cumplimiento.
          </p>
        </div>
      </div>

      {/* Base HH — denominador de todos los indicadores SGSSO */}
      <div className="bg-white border border-slate-200 rounded-lg p-6" data-testid="hse-basehh-card">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <Label className="overline-label">Base HH del período · denominador SGSSO</Label>
            <p className="text-xs text-slate-500 mt-1 mb-3">
              Horas-Hombre trabajadas en el período. Se usa como denominador en TRIR / LTIFR / IF / IS.
              La NOM-031-STPS usa <b>2,000,000 HH</b> como referencia; OSHA típicamente 200,000.
            </p>
          </div>
          {canManage && (
            <Button size="sm" variant="outline" onClick={() => setAutoOpen((v) => !v)}
                    data-testid="hse-basehh-auto-toggle"
                    className="border-blue-200 text-blue-900 gap-1.5">
              <Calculator size={14} weight="bold" /> {autoOpen ? "Cerrar" : "Calcular automáticamente"}
            </Button>
          )}
        </div>
        <div className="flex items-end gap-2 flex-wrap max-w-2xl">
          <div className="flex-1 min-w-[220px]">
            <Input
              type="number" step="1000" min="0" value={manHours}
              data-testid="hse-basehh-input"
              onChange={(e) => { setManHours(e.target.value); setManHoursDirty(true); }}
              disabled={!canManage}
              className="text-lg font-mono font-bold text-slate-900"
            />
            <p className="text-[10px] text-slate-400 mt-1 font-mono">
              Valor actual: {Number(manHours).toLocaleString()} HH
            </p>
            {lastAuto && (
              <div className="mt-2 flex items-start gap-1.5 bg-emerald-50 border border-emerald-200 rounded px-2 py-1.5"
                   data-testid="hse-basehh-last-auto">
                <span className="text-emerald-700 font-bold text-xs mt-px">↻</span>
                <div className="text-[10px] leading-snug text-emerald-900">
                  <span className="font-bold">Último auto-recálculo:</span>{" "}
                  <span className="font-mono">
                    {new Date(lastAuto.at).toLocaleString("es-MX", {
                      day: "2-digit", month: "short", year: "numeric",
                      hour: "2-digit", minute: "2-digit", timeZoneName: "short",
                    })}
                  </span>
                  {lastAuto.formula && (
                    <div className="text-emerald-800 font-mono mt-0.5">
                      {lastAuto.formula}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
          {canManage && (
            <Button onClick={saveManHours} disabled={!manHoursDirty}
                    data-testid="hse-basehh-save"
                    className="bg-blue-900 hover:bg-blue-800 gap-2 h-10">
              <FloppyDisk size={14} weight="bold" /> Guardar base HH
            </Button>
          )}
        </div>

        {autoOpen && canManage && (
          <div className="mt-4 border border-blue-200 rounded-lg bg-blue-50 p-4 space-y-3"
               data-testid="hse-basehh-auto-panel">
            <p className="text-xs font-bold text-blue-900 uppercase tracking-widest">
              Estimación automática desde RRHH
            </p>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
              <div><Label className="text-[10px]">Desde</Label>
                <Input type="date" value={autoForm.date_from} className="h-9"
                  data-testid="hse-basehh-auto-from"
                  onChange={(e) => setAutoForm({ ...autoForm, date_from: e.target.value })} />
              </div>
              <div><Label className="text-[10px]">Hasta</Label>
                <Input type="date" value={autoForm.date_to} className="h-9"
                  data-testid="hse-basehh-auto-to"
                  onChange={(e) => setAutoForm({ ...autoForm, date_to: e.target.value })} />
              </div>
              <div><Label className="text-[10px]">Horas / turno</Label>
                <Input type="number" step="0.5" min="1" max="24"
                  value={autoForm.hours_per_shift} className="h-9"
                  data-testid="hse-basehh-auto-hps"
                  onChange={(e) => setAutoForm({ ...autoForm, hours_per_shift: Number(e.target.value) })} />
              </div>
              <div><Label className="text-[10px]">Turnos / día</Label>
                <Input type="number" step="1" min="1" max="4"
                  value={autoForm.shifts_per_day} className="h-9"
                  data-testid="hse-basehh-auto-spd"
                  onChange={(e) => setAutoForm({ ...autoForm, shifts_per_day: Number(e.target.value) })} />
              </div>
              <div><Label className="text-[10px]">Fuente</Label>
                <select
                  value={autoForm.headcount_source} className="h-9 w-full border rounded px-2 text-sm"
                  data-testid="hse-basehh-auto-source"
                  onChange={(e) => setAutoForm({ ...autoForm, headcount_source: e.target.value })}
                >
                  <option value="hr">Activos en HR</option>
                  <option value="attendance">Asistencias reales</option>
                </select>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button size="sm" onClick={previewAuto} disabled={autoBusy}
                      data-testid="hse-basehh-auto-preview"
                      className="bg-blue-700 hover:bg-blue-600 text-white gap-1.5">
                {autoBusy ? "Calculando…" : "Previsualizar"}
              </Button>
              {autoPreview && (
                <Button size="sm" onClick={applyAuto} variant="outline"
                        data-testid="hse-basehh-auto-apply"
                        className="border-emerald-500 text-emerald-700 gap-1.5">
                  ✓ Aplicar {Number(autoPreview.man_hours).toLocaleString()} HH
                </Button>
              )}
            </div>
            {autoPreview && (
              <div className="bg-white border border-blue-200 rounded p-2 text-xs font-mono text-slate-700"
                   data-testid="hse-basehh-auto-formula">
                <b className="text-blue-900">Fórmula:</b> {autoPreview.formula}
              </div>
            )}
          </div>
        )}
      </div>

      <div className="bg-white border border-slate-200 rounded-lg p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {FIELDS.map((f) => (
            <div key={f.k}>
              <Label className="overline-label">{f.l}</Label>
              <Input
                data-testid={`kpi-target-${f.k}`}
                type="number"
                step={f.step}
                min={0}
                value={form[f.k] ?? 0}
                onChange={(e) => { setForm({ ...form, [f.k]: e.target.value }); setDirty(true); }}
                disabled={!canManage}
              />
              <p className="text-[11px] text-slate-500 mt-1 italic">{f.hint}</p>
            </div>
          ))}
        </div>
        {canManage && (
          <Button
            data-testid="kpi-targets-save"
            onClick={save}
            disabled={!dirty}
            className="mt-6 bg-blue-900 hover:bg-blue-800 gap-2"
          ><FloppyDisk size={14} weight="bold" /> Guardar objetivos</Button>
        )}
        {!canManage && (
          <p className="text-xs text-slate-400 italic mt-4">
            Solo administradores y gerentes pueden modificar los objetivos.
          </p>
        )}
      </div>
    </div>
  );
}
